import React, { useState } from 'react';
import { FileText, MessageCircle, Undo2 } from 'lucide-react';
import { toast } from 'sonner';
import { sharePdfToWhatsApp } from '../../../lib/waShare';
import { ReturnDialog } from './PoForms';
import { Chip, Drawer, F, field, ghost, primary, rs, Stat, safe, useApi } from './ui';

const PayDialog = ({ bill, onClose, onSaved }) => {
  const { call } = useApi();
  const [p, setP] = useState({ amount: bill.balance, method: 'upi', reference: '', date: new Date().toISOString().slice(0, 10) });
  const save = safe(async () => { await call('post', `/purchasing/bills/${bill.id}/payments`, { ...p, amount: Number(p.amount) }); toast.success('Payment recorded'); onSaved(); });
  return (
    <Drawer title={`Pay ${bill.number}`} onClose={onClose} testid="pay-dialog" footer={<button className={primary} onClick={save} data-testid="pay-save">Record payment</button>}>
      <p className="text-sm">Still owed: <b>{rs(bill.balance)}</b></p>
      <div className="grid gap-3 sm:grid-cols-2">
        <F label="Amount ₹"><input type="number" value={p.amount} onChange={(e) => setP({ ...p, amount: e.target.value })} className={field} data-testid="pay-amount" /></F>
        <F label="Method"><select value={p.method} onChange={(e) => setP({ ...p, method: e.target.value })} className={field} data-testid="pay-method">
          {[['cash', 'Cash'], ['upi', 'UPI'], ['bank', 'Bank transfer'], ['cheque', 'Cheque']].map(([k, l]) => <option key={k} value={k}>{l}</option>)}</select></F>
        <F label="Reference"><input value={p.reference} onChange={(e) => setP({ ...p, reference: e.target.value })} className={field} data-testid="pay-ref" /></F>
        <F label="Date"><input type="date" value={p.date} onChange={(e) => setP({ ...p, date: e.target.value })} className={field} data-testid="pay-date" /></F>
      </div>
    </Drawer>
  );
};

const VendorBills = ({ row, vendors, items, onClose, reload }) => {
  const { call, blob, open } = useApi();
  const [bills, setBills] = useState([]);
  const [pay, setPay] = useState(null);
  const [ret, setRet] = useState(null);
  const load = React.useCallback(() => call('get', `/purchasing/bills?vendor_id=${row.vendor_id}`).then(setBills), [call, row.vendor_id]);
  React.useEffect(() => { load(); }, [load]);
  const v = vendors.find((x) => x.id === row.vendor_id) || {};
  const after = () => { setPay(null); setRet(null); load(); reload(); };
  const share = () => sharePdfToWhatsApp({ getPdf: () => blob(`/purchasing/vendors/${row.vendor_id}/statement/pdf`), filename: `statement-${row.vendor_name}.pdf`,
    title: 'Vendor statement', phone: v.mobile, message: `Hello ${v.contact_person || row.vendor_name}, here is our account statement.` });
  return (
    <Drawer title={row.vendor_name} onClose={onClose} testid="vendor-bills">
      <div className="flex flex-wrap gap-2">
        <button className={ghost} onClick={() => open(`/purchasing/vendors/${row.vendor_id}/statement/pdf`)} data-testid="statement-pdf"><FileText size={14} /> Statement PDF</button>
        <button className={ghost} onClick={share} data-testid="statement-wa"><MessageCircle size={14} /> Share statement</button>
      </div>
      {bills.map((b) => (
        <div key={b.id} className="rounded-lg border p-3 text-sm" data-testid={`bill-row-${b.id}`}>
          <div className="flex flex-wrap items-center gap-2"><b className="font-mono">{b.number}</b><Chip s={b.status} /><span className="text-slate-500">{b.invoice_no} · {b.bill_date} · due {b.due_date}</span>
            <span className="ml-auto">{rs(b.total)} · owed <b>{rs(b.balance)}</b></span></div>
          <div className="mt-2 flex gap-2">
            {b.balance > 0 && <button className={primary} onClick={() => setPay(b)} data-testid={`bill-pay-${b.id}`}>Record payment</button>}
            <button className={ghost} onClick={() => setRet({ vendor_id: b.vendor_id, bill_id: b.id, lines: b.lines })} data-testid={`bill-return-${b.id}`}><Undo2 size={14} /> Return</button>
          </div>
        </div>
      ))}
      {pay && <PayDialog bill={pay} onClose={() => setPay(null)} onSaved={after} />}
      {ret && <ReturnDialog source={ret} items={items} onClose={() => setRet(null)} onSaved={after} />}
    </Drawer>
  );
};

export const PayablesTab = ({ board, vendors, items, reload }) => {
  const [row, setRow] = useState(null);
  if (!board) return null;
  return (
    <div className="space-y-4" data-testid="payables-tab">
      <div className="grid grid-cols-3 gap-3">
        <Stat label="Payable" value={rs(board.payable)} testid="owe-payable" />
        <Stat label="Overdue" value={rs(board.overdue)} tone="text-red-600" testid="owe-overdue" />
        <Stat label="Due this week" value={rs(board.due_week)} tone="text-amber-700" testid="owe-week" />
      </div>
      <div className="overflow-x-auto rounded-xl border bg-white">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-left text-xs uppercase text-slate-500"><tr><th className="p-3">Vendor</th><th className="p-3 text-right">Payable</th><th className="p-3 text-right">Overdue</th><th className="p-3 text-right">Due this week</th><th className="p-3">Bills</th></tr></thead>
          <tbody>{board.rows.map((r) => (
            <tr key={r.vendor_id} onClick={() => setRow(r)} className="cursor-pointer border-t hover:bg-slate-50" data-testid={`owe-row-${r.vendor_id}`}>
              <td className="p-3 font-semibold">{r.vendor_name}{r.over_limit && <span className="ml-2 rounded-full bg-red-100 px-2 text-[11px] text-red-700" data-testid={`owe-overlimit-${r.vendor_id}`}>Over credit limit</span>}</td>
              <td className="p-3 text-right">{rs(r.payable)}</td><td className="p-3 text-right text-red-600">{rs(r.overdue)}</td><td className="p-3 text-right">{rs(r.due_week)}</td><td className="p-3">{r.bills}</td>
            </tr>))}</tbody>
        </table>
        {!board.rows.length && <p className="p-8 text-center text-sm text-slate-500">Nothing owed right now.</p>}
      </div>
      {row && <VendorBills row={row} vendors={vendors} items={items} onClose={() => setRow(null)} reload={reload} />}
    </div>
  );
};

export const ReturnsTab = ({ returns }) => {
  const { open } = useApi();
  return (
    <div className="space-y-2" data-testid="returns-tab">
      {returns.map((r) => (
        <div key={r.id} className="flex flex-wrap items-center gap-3 rounded-xl border bg-white p-4 text-sm" data-testid={`return-row-${r.id}`}>
          <b className="font-mono">{r.number}</b><span>{r.vendor_name}</span><span className="text-slate-500">{r.return_date} · {r.lines.map((l) => `${l.name} ×${l.qty}`).join(', ')}</span>
          <span className="rounded-full bg-slate-100 px-2 text-[11px]">{r.settle === 'refund' ? 'Refund received' : 'Adjusted in dues'}</span>
          <b className="ml-auto">{rs(r.total)}</b>
          <button className={ghost} onClick={() => open(`/purchasing/returns/${r.id}/pdf`)} data-testid={`return-pdf-${r.id}`}><FileText size={14} /> Debit note</button>
        </div>
      ))}
      {!returns.length && <p className="py-10 text-center text-sm text-slate-500">No returns yet.</p>}
    </div>
  );
};
